import {
    CloudUploadOutlined,
    DeleteOutlined,
    DrawOutlined,
    TextFieldsOutlined,
    UploadFileOutlined,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    MenuItem,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { Form, Formik, useFormikContext, type FormikHelpers } from 'formik';
import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type MouseEvent as ReactMouseEvent,
    type MutableRefObject,
    type ReactElement,
    type ReactNode,
    type TouchEvent as ReactTouchEvent,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import SubmitButton from 'components/common/formikForm/SubmitButton';
import { SignatureMetadataFields } from 'views/v2/signatures/components/signatureMetadataFields';
import { SignaturePreview } from 'views/v2/signatures/components/signaturePreview';

import {
    SIGNATURE_COLORS,
    SIGNATURE_CONSTRAINTS,
    SIGNATURE_FONTS,
    SIGNATURE_SIZES,
    fileToBase64,
    getFontFamilyCSS,
    validateImageFile,
} from 'config/signatureFonts';
import { setAlert } from 'redux/alert';
import { createSignature } from 'redux/signatures/signatures.middleware';
import { selectSignaturesCreating } from 'redux/signatures/signatures.slice';
import type { AppDispatch } from 'redux/store';
import type {
    CreateSignatureFormInterface,
    SignatureCreateRequest,
    SignatureCreationMethod,
} from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';
import {
    createSignatureDefaultValues,
    createSignatureValidationSchema,
    getMethodFieldDefaults,
} from 'utils/validations/signature';

interface CreateSignatureFormProps {
    onSuccess?: () => void;
    onCancel?: () => void;
}

const sizeOptions = SIGNATURE_SIZES.map((size) => ({
    label: size.description ? `${size.name} — ${size.description}` : size.name,
    value: size.value,
}));

const METHOD_OPTIONS: {
    value: SignatureCreationMethod;
    label: string;
    icon: ReactElement;
}[] = [
    { value: 'typed', label: 'Text', icon: <TextFieldsOutlined fontSize="small" /> },
    { value: 'drawn', label: 'Draw', icon: <DrawOutlined fontSize="small" /> },
    { value: 'uploaded', label: 'Upload', icon: <UploadFileOutlined fontSize="small" /> },
];

// ─── Method toggle (clears method-specific fields) ───────────────────────────

function MethodToggle({
    disabled,
    onMethodChange,
}: {
    disabled: boolean;
    onMethodChange: (method: SignatureCreationMethod) => void;
}) {
    const { values, setValues } = useFormikContext<CreateSignatureFormInterface>();

    const handleChange = (
        _event: ReactMouseEvent<HTMLElement>,
        next: SignatureCreationMethod | null
    ) => {
        if (!next || next === values.creation_method) return;

        // Keep common metadata; reset method-specific fields
        void setValues(
            {
                ...values,
                ...getMethodFieldDefaults(next),
            },
            false
        );
        onMethodChange(next);
    };

    return (
        <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.25 }}>
                Signature method
            </Typography>
            <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={values.creation_method}
                onChange={handleChange}
                disabled={disabled}
                sx={{
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 0.75,
                        px: 1.5,
                        gap: 0.75,
                        flex: { xs: '1 1 30%', sm: 1 },
                        borderRadius: '8px !important',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&.Mui-selected': {
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            borderColor: 'primary.main',
                            '&:hover': {
                                bgcolor: 'primary.dark',
                            },
                        },
                    },
                }}
            >
                {METHOD_OPTIONS.map((option) => (
                    <ToggleButton key={option.value} value={option.value} aria-label={option.label}>
                        {option.icon}
                        {option.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>
    );
}

// ─── Shared layout helpers ───────────────────────────────────────────────────

const methodPanelSx = {
    p: 2,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    bgcolor: 'transparent',
    minHeight: 160,
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
};

function LivePreviewShell({
    children,
    label = 'Live preview',
}: {
    children: ReactNode;
    label?: string;
}) {
    return (
        <Box sx={methodPanelSx}>
            <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 2, alignSelf: 'flex-start' }}
            >
                {label}
            </Typography>
            <Box
                sx={{
                    width: '100%',
                    flex: 1,
                    p: 2,
                    borderRadius: 1.5,
                    border: '1px dashed',
                    borderColor: 'divider',
                    bgcolor: 'transparent',
                    minHeight: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

function MethodStack({ fields, preview }: { fields: ReactNode; preview: ReactNode }) {
    return (
        <Stack spacing={2}>
            {fields}
            {preview}
        </Stack>
    );
}

// ─── Typed fields + live preview ─────────────────────────────────────────────

function TypedFields({ disabled }: { disabled: boolean }) {
    const { values, setFieldValue, setFieldTouched, errors, touched, submitCount } =
        useFormikContext<CreateSignatureFormInterface>();

    const fontError = Boolean(errors.font_family && (touched.font_family || submitCount > 0));
    const colorError = Boolean(errors.color && (touched.color || submitCount > 0));

    return (
        <MethodStack
            fields={
                <Stack spacing={2}>
                    <FormField
                        name="typed_text"
                        label="Signature Text"
                        required
                        disabled={disabled}
                        placeholder="Enter your name"
                        autoFocus
                    />

                    <FormGridLayout>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Font"
                            name="font_family"
                            value={values.font_family}
                            disabled={disabled}
                            error={fontError}
                            helperText={fontError ? String(errors.font_family) : ' '}
                            onChange={(e) => {
                                void setFieldValue('font_family', e.target.value, true);
                                void setFieldTouched('font_family', true, false);
                            }}
                        >
                            {SIGNATURE_FONTS.map((font) => (
                                <MenuItem key={font.value} value={font.value}>
                                    <Box
                                        component="span"
                                        sx={{ fontFamily: getFontFamilyCSS(font.value) }}
                                    >
                                        {font.name}
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Color"
                            name="color"
                            value={values.color}
                            disabled={disabled}
                            error={colorError}
                            helperText={colorError ? String(errors.color) : ' '}
                            onChange={(e) => {
                                void setFieldValue('color', e.target.value, true);
                                void setFieldTouched('color', true, false);
                            }}
                        >
                            {SIGNATURE_COLORS.map((color) => (
                                <MenuItem key={color.value} value={color.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box
                                            sx={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: 0.75,
                                                bgcolor: color.value,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        />
                                        {color.name}
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    </FormGridLayout>

                    <FormField
                        name="size"
                        label="Size"
                        type="select"
                        options={sizeOptions}
                        disabled={disabled}
                    />
                </Stack>
            }
            preview={
                <LivePreviewShell>
                    <SignaturePreview
                        signature={{
                            creation_method: 'typed',
                            typed_text: values.typed_text,
                            font_family: values.font_family,
                            color: values.color,
                            size: values.size,
                            title: values.title,
                        }}
                        showTitle
                        maxHeight={120}
                    />
                </LivePreviewShell>
            }
        />
    );
}

// ─── Draw canvas ─────────────────────────────────────────────────────────────

function DrawnFields({
    disabled,
    hasDrawn,
    setHasDrawn,
    canvasError,
    clearError,
    canvasKey,
}: {
    disabled: boolean;
    hasDrawn: boolean;
    setHasDrawn: (value: boolean) => void;
    canvasError: string | null;
    clearError: () => void;
    canvasKey: number;
}) {
    const { values, setFieldValue, setFieldTouched, errors, touched, submitCount } =
        useFormikContext<CreateSignatureFormInterface>();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);

    const colorError = Boolean(errors.color && (touched.color || submitCount > 0));

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const container = canvas.parentElement;
        if (container) {
            canvas.width = container.clientWidth;
            canvas.height = Math.min(200, Math.max(160, Math.round(container.clientWidth * 0.35)));
        }

        const ctx = canvas.getContext('2d');
        if (ctx) {
            // Transparent canvas so previews match typed (no white fill)
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
        setHasDrawn(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- remount via canvasKey
    }, [canvasKey]);

    const getCoordinates = (event: MouseEvent | TouchEvent): { x: number; y: number } | null => {
        const canvas = canvasRef.current;
        if (!canvas) return null;

        const rect = canvas.getBoundingClientRect();
        const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
        const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

        return {
            x: clientX - rect.left,
            y: clientY - rect.top,
        };
    };

    const startDrawing = (event: ReactMouseEvent | ReactTouchEvent) => {
        if (disabled) return;
        event.preventDefault();
        setIsDrawing(true);
        setHasDrawn(true);
        clearError();

        const coords = getCoordinates(event.nativeEvent as MouseEvent | TouchEvent);
        if (!coords) return;

        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) {
            ctx.beginPath();
            ctx.moveTo(coords.x, coords.y);
        }
    };

    const draw = (event: ReactMouseEvent | ReactTouchEvent) => {
        if (!isDrawing || disabled) return;
        event.preventDefault();

        const coords = getCoordinates(event.nativeEvent as MouseEvent | TouchEvent);
        if (!coords) return;

        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) {
            ctx.strokeStyle = values.color;
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.lineTo(coords.x, coords.y);
            ctx.stroke();
        }
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            setHasDrawn(false);
        }
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        (canvas as HTMLCanvasElement & { __exportSvg?: () => string }).__exportSvg = () => {
            const dataURL = canvas.toDataURL('image/png');
            return `<svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}">
                        <image href="${dataURL}" width="${canvas.width}" height="${canvas.height}" />
                    </svg>`;
        };
    }, [canvasKey]);

    return (
        <MethodStack
            fields={
                <Stack spacing={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Drawing options
                    </Typography>

                    <FormGridLayout>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Pen Color"
                            name="color"
                            value={values.color}
                            disabled={disabled}
                            error={colorError}
                            helperText={colorError ? String(errors.color) : ' '}
                            onChange={(e) => {
                                void setFieldValue('color', e.target.value, true);
                                void setFieldTouched('color', true, false);
                            }}
                        >
                            {SIGNATURE_COLORS.map((color) => (
                                <MenuItem key={color.value} value={color.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box
                                            sx={{
                                                width: 18,
                                                height: 18,
                                                borderRadius: 0.75,
                                                bgcolor: color.value,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        />
                                        {color.name}
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>

                        <FormField
                            name="size"
                            label="Display Size"
                            type="select"
                            options={sizeOptions}
                            disabled={disabled}
                        />
                    </FormGridLayout>

                    <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        startIcon={<DeleteOutlined />}
                        onClick={clearCanvas}
                        disabled={disabled || !hasDrawn}
                        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                    >
                        Clear drawing
                    </Button>

                    {canvasError ? (
                        <Typography variant="caption" color="error">
                            {canvasError}
                        </Typography>
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            Use your mouse or finger on the canvas to sign
                        </Typography>
                    )}
                </Stack>
            }
            preview={
                <Box sx={{ ...methodPanelSx, p: 0, overflow: 'hidden', alignItems: 'stretch' }}>
                    <Box
                        sx={{
                            px: 2,
                            pt: 2,
                            pb: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="subtitle2" color="text.secondary">
                            Draw your signature
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            mx: 2,
                            mb: 2,
                            border: '1px dashed',
                            borderColor: canvasError ? 'error.main' : 'divider',
                            borderRadius: 1.5,
                            overflow: 'hidden',
                            bgcolor: 'transparent',
                            minHeight: 160,
                        }}
                    >
                        <canvas
                            ref={canvasRef}
                            id="signature-draw-canvas"
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={stopDrawing}
                            style={{
                                cursor: disabled ? 'not-allowed' : 'crosshair',
                                width: '100%',
                                display: 'block',
                                touchAction: 'none',
                                background: 'transparent',
                            }}
                        />
                    </Box>
                </Box>
            }
        />
    );
}

function exportCanvasSvg(): string {
    const canvas = document.getElementById('signature-draw-canvas') as
        | (HTMLCanvasElement & { __exportSvg?: () => string })
        | null;
    if (canvas?.__exportSvg) {
        return canvas.__exportSvg();
    }
    if (!canvas) return '';
    const dataURL = canvas.toDataURL('image/png');
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}">
      <image href="${dataURL}" width="${canvas.width}" height="${canvas.height}" />
    </svg>`;
}

// ─── Upload fields ───────────────────────────────────────────────────────────

function UploadFields({
    disabled,
    uploadedFile,
    previewURL,
    fileError,
    isDragging,
    fileInputRef,
    onFileSelect,
    onRemoveFile,
    onDragState,
}: {
    disabled: boolean;
    uploadedFile: File | null;
    previewURL: string;
    fileError: string | null;
    isDragging: boolean;
    fileInputRef: MutableRefObject<HTMLInputElement | null>;
    onFileSelect: (file: File) => void;
    onRemoveFile: () => void;
    onDragState: (dragging: boolean) => void;
}) {
    return (
        <MethodStack
            fields={
                <Stack spacing={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Upload signature image
                    </Typography>

                    <Box
                        onDragEnter={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!disabled) onDragState(true);
                        }}
                        onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onDragState(false);
                        }}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onDragState(false);
                            if (disabled) return;
                            const file = e.dataTransfer.files?.[0];
                            if (file) onFileSelect(file);
                        }}
                        onClick={() => {
                            if (!disabled) fileInputRef.current?.click();
                        }}
                        sx={{
                            p: 2,
                            border: '2px dashed',
                            borderColor: isDragging
                                ? 'primary.main'
                                : fileError
                                  ? 'error.main'
                                  : 'divider',
                            borderRadius: 2,
                            bgcolor: isDragging ? 'action.hover' : 'transparent',
                            textAlign: 'center',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            transition: 'border-color 0.2s ease, background-color 0.2s ease',
                            opacity: disabled ? 0.6 : 1,
                            minHeight: 120,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': disabled
                                ? undefined
                                : {
                                      borderColor: 'primary.main',
                                      bgcolor: 'action.hover',
                                  },
                        }}
                    >
                        <Box>
                            <CloudUploadOutlined
                                sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }}
                            />
                            <Typography variant="body2" gutterBottom>
                                Drag and drop, or click to browse
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                            >
                                PNG or JPEG, max {SIGNATURE_CONSTRAINTS.MAX_IMAGE_SIZE_MB}MB
                            </Typography>
                            {uploadedFile ? (
                                <Typography
                                    variant="caption"
                                    color="primary"
                                    sx={{ mt: 1, display: 'block', fontWeight: 600 }}
                                >
                                    {uploadedFile.name}
                                </Typography>
                            ) : null}
                        </Box>
                        <input
                            ref={(element) => {
                                fileInputRef.current = element;
                            }}
                            type="file"
                            accept={SIGNATURE_CONSTRAINTS.ALLOWED_IMAGE_TYPES.join(',')}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) onFileSelect(file);
                            }}
                            style={{ display: 'none' }}
                        />
                    </Box>

                    {fileError ? (
                        <Typography variant="caption" color="error">
                            {fileError}
                        </Typography>
                    ) : null}

                    <FormGridLayout>
                        <FormField
                            name="size"
                            label="Display Size"
                            type="select"
                            options={sizeOptions}
                            disabled={disabled}
                        />
                        {uploadedFile ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    fullWidth
                                    startIcon={<DeleteOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveFile();
                                    }}
                                    disabled={disabled}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Remove image
                                </Button>
                            </Box>
                        ) : (
                            <Box />
                        )}
                    </FormGridLayout>

                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography variant="caption" component="div">
                            <strong>Best practices</strong>
                            <br />• Prefer transparent PNG so previews match typed signatures
                            <br />• Ensure good contrast
                            <br />• Recommended size around 300×100px
                        </Typography>
                    </Alert>
                </Stack>
            }
            preview={
                <LivePreviewShell label="Image preview">
                    {previewURL ? (
                        <Box
                            component="img"
                            src={previewURL}
                            alt="Signature preview"
                            sx={{
                                maxWidth: '100%',
                                maxHeight: 120,
                                objectFit: 'contain',
                                mixBlendMode: 'multiply',
                                bgcolor: 'transparent',
                            }}
                        />
                    ) : (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontStyle: 'italic' }}
                        >
                            Upload an image to see a preview
                        </Typography>
                    )}
                </LivePreviewShell>
            }
        />
    );
}

// ─── Main form ───────────────────────────────────────────────────────────────

export default function CreateSignatureForm({ onSuccess, onCancel }: CreateSignatureFormProps) {
    const dispatch = useDispatch<AppDispatch>();
    const isCreating = useSelector(selectSignaturesCreating);

    // Draw state
    const [hasDrawn, setHasDrawn] = useState(false);
    const [canvasError, setCanvasError] = useState<string | null>(null);
    const [canvasKey, setCanvasKey] = useState(0);

    // Upload state
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [previewURL, setPreviewURL] = useState('');
    const [fileError, setFileError] = useState<string | null>(null);

    const clearMethodExtras = useCallback(() => {
        setHasDrawn(false);
        setCanvasError(null);
        setCanvasKey((k) => k + 1);
        setUploadedFile(null);
        setPreviewURL('');
        setFileError(null);
        setIsDragging(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const handleMethodChange = useCallback(
        (_method: SignatureCreationMethod) => {
            clearMethodExtras();
        },
        [clearMethodExtras]
    );

    const handleFileSelect = (file: File) => {
        const validation = validateImageFile(file);
        if (!validation.valid) {
            setFileError(validation.error || 'Invalid file');
            dispatch(
                setAlert({
                    message: validation.error || 'Invalid file',
                    type: 'error',
                })
            );
            return;
        }

        setFileError(null);
        setUploadedFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewURL((e.target?.result as string) || '');
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
        setPreviewURL('');
        setFileError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (
        values: CreateSignatureFormInterface,
        formikHelpers: FormikHelpers<CreateSignatureFormInterface>
    ) => {
        const method = values.creation_method;

        if (method === 'drawn' && !hasDrawn) {
            setCanvasError('Please draw your signature');
            dispatch(setAlert({ message: 'Please draw your signature', type: 'error' }));
            return;
        }

        if (method === 'uploaded' && !uploadedFile) {
            setFileError('Please upload a signature image');
            dispatch(setAlert({ message: 'Please upload a signature image', type: 'error' }));
            return;
        }

        try {
            let payload: SignatureCreateRequest;

            if (method === 'typed') {
                payload = {
                    creation_method: 'typed',
                    typed_text: values.typed_text.trim(),
                    font_family: values.font_family,
                    color: values.color,
                    size: values.size,
                    display_name: values.display_name.trim() || undefined,
                    title: values.title.trim() || undefined,
                    signature_name: values.signature_name.trim() || undefined,
                    is_default: values.is_default,
                };
            } else if (method === 'drawn') {
                payload = {
                    creation_method: 'drawn',
                    drawn_svg_data: exportCanvasSvg(),
                    color: values.color,
                    size: values.size,
                    display_name: values.display_name.trim() || undefined,
                    title: values.title.trim() || undefined,
                    signature_name: values.signature_name.trim() || undefined,
                    is_default: values.is_default,
                };
            } else {
                const base64Data = await fileToBase64(uploadedFile as File);
                payload = {
                    creation_method: 'uploaded',
                    uploaded_image_base64: base64Data,
                    uploaded_image_mime_type: (uploadedFile as File).type,
                    size: values.size,
                    display_name: values.display_name.trim() || undefined,
                    title: values.title.trim() || undefined,
                    signature_name: values.signature_name.trim() || undefined,
                    is_default: values.is_default,
                };
            }

            await dispatch(createSignature(payload)).unwrap();
            formikHelpers.resetForm();
            clearMethodExtras();
            dispatch(setAlert({ message: 'Signature created successfully', type: 'success' }));
            onSuccess?.();
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'Failed to create signature'),
                    type: 'error',
                })
            );
        }
    };

    return (
        <Formik
            initialValues={createSignatureDefaultValues}
            validationSchema={createSignatureValidationSchema}
            onSubmit={handleSubmit}
        >
            {({ values }) => {
                const method = values.creation_method;
                const isSubmitDisabled =
                    !isCreating &&
                    ((method === 'drawn' && !hasDrawn) || (method === 'uploaded' && !uploadedFile));

                return (
                    <Form>
                        <Stack spacing={3}>
                            {/* Common metadata */}
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    sx={{ mb: 1.25 }}
                                >
                                    Signature details
                                </Typography>
                                <SignatureMetadataFields disabled={isCreating} layout="grid" />
                            </Box>

                            {/* Method selection */}
                            <MethodToggle
                                disabled={isCreating}
                                onMethodChange={handleMethodChange}
                            />

                            {/* Method-specific fields */}
                            <Box>
                                {method === 'typed' ? <TypedFields disabled={isCreating} /> : null}

                                {method === 'drawn' ? (
                                    <DrawnFields
                                        disabled={isCreating}
                                        hasDrawn={hasDrawn}
                                        setHasDrawn={setHasDrawn}
                                        canvasError={canvasError}
                                        clearError={() => setCanvasError(null)}
                                        canvasKey={canvasKey}
                                    />
                                ) : null}

                                {method === 'uploaded' ? (
                                    <UploadFields
                                        disabled={isCreating}
                                        uploadedFile={uploadedFile}
                                        previewURL={previewURL}
                                        fileError={fileError}
                                        isDragging={isDragging}
                                        fileInputRef={fileInputRef}
                                        onFileSelect={handleFileSelect}
                                        onRemoveFile={handleRemoveFile}
                                        onDragState={setIsDragging}
                                    />
                                ) : null}
                            </Box>

                            {/* Actions */}
                            <Stack
                                direction={{ xs: 'column-reverse', sm: 'row' }}
                                spacing={1.5}
                                sx={{
                                    justifyContent: 'flex-end',
                                    pt: 1,
                                    pb: { xs: 2, sm: 0 },
                                }}
                            >
                                {onCancel ? (
                                    <Button
                                        variant="outlined"
                                        onClick={onCancel}
                                        disabled={isCreating}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Cancel
                                    </Button>
                                ) : null}
                                <SubmitButton loading={isCreating} disabled={isSubmitDisabled}>
                                    createSignature
                                </SubmitButton>
                            </Stack>
                        </Stack>
                    </Form>
                );
            }}
        </Formik>
    );
}
